import type { StoryGenre, StoryStatus } from "@/lib/actions/story-actions";
import type { CrownDiscoveryFilter } from "@/lib/spotlights/discovery-filters";

// ─── Client-side catalog filtering ───────────────────────
// Pure, client-safe mirror of `publicStoryFilterConditions` in
// story-actions.ts. The browse/genre pages are statically rendered (ISR) with
// the FULL public catalog embedded, and filtering happens here in the browser
// instead of per-request SQL — so filter clicks never invoke a server
// function. Semantics MUST stay in lockstep with the SQL:
//   - genres: AND — a story must carry EVERY selected genre
//   - tags:   AND — a story must carry EVERY selected tag
//   - status: exact match
//   - q:      whitespace tokens, each token must appear (case-insensitive)
//             in the title OR the author name

/** The facet fields the filter reads — CatalogStory is a superset. */
export interface CatalogStoryFacets {
    status: StoryStatus;
    genres: StoryGenre[];
    tags: string[];
}

export interface CatalogFilterState {
    genres?: StoryGenre[];
    discovery?: CrownDiscoveryFilter;
    status?: StoryStatus;
    tags?: string[];
    q?: string;
}

export interface CrownDiscoveryCatalog {
    mostSpotlighted: readonly { id: string }[];
    ascending: readonly { id: string }[];
    freshFinds: readonly { id: string }[];
}

const EMPTY_CROWN_DISCOVERY: CrownDiscoveryCatalog = {
    mostSpotlighted: [],
    ascending: [],
    freshFinds: [],
};

function discoveryStories(
    discovery: CrownDiscoveryCatalog,
    filter: CrownDiscoveryFilter,
) {
    if (filter === "most-crowned") return discovery.mostSpotlighted;
    if (filter === "ascending") return discovery.ascending;
    return discovery.freshFinds;
}

export function filterCatalogStories<
    T extends CatalogStoryFacets & {
        id: string;
        title: string;
        authorName: string | null;
    },
>(
    stories: T[],
    filters: CatalogFilterState,
    crownDiscovery: CrownDiscoveryCatalog = EMPTY_CROWN_DISCOVERY,
): T[] {
    const genres = filters.genres ?? [];
    const tags = filters.tags ?? [];
    const tokens = (filters.q ?? "").trim().toLowerCase().split(/\s+/).filter(Boolean);

    const facetMatches = stories.filter((s) => {
        if (genres.some((g) => !s.genres.includes(g))) return false;
        if (tags.some((t) => !s.tags.includes(t))) return false;
        if (filters.status && s.status !== filters.status) return false;
        if (tokens.length > 0) {
            const title = s.title.toLowerCase();
            const author = (s.authorName ?? "").toLowerCase();
            if (
                tokens.some(
                    (tok) => !title.includes(tok) && !author.includes(tok),
                )
            ) {
                return false;
            }
        }
        return true;
    });

    if (!filters.discovery) return facetMatches;

    const matchesById = new Map(
        facetMatches.map((story) => [story.id, story]),
    );
    const seen = new Set<string>();
    const rankedMatches: T[] = [];
    for (const { id } of discoveryStories(
        crownDiscovery,
        filters.discovery,
    )) {
        const story = matchesById.get(id);
        if (!story || seen.has(id)) continue;
        seen.add(id);
        rankedMatches.push(story);
    }
    return rankedMatches;
}
